import { isTypeLabelOwnedByExcluded } from "./bundle-ownership";
import { AgentTierConfig } from "../agent-config-options";

/**
 * Valid funnel-tier values. Lower numbers dispatch first when priority
 * is tied. See `DEFAULT_AGENT_TIERS` for the role of each tier.
 */
export const AGENT_TIER_VALUES = [0, 1, 2, 3, 4] as const;
export type AgentTier = (typeof AGENT_TIER_VALUES)[number];

/**
 * Human-readable role name for each funnel tier. Rendered into the
 * orchestrator-conventions rule and surfaced in generated `PICK` lines
 * so agents and humans can read the sort key at a glance.
 */
export const AGENT_TIER_ROLES: Readonly<Record<AgentTier, string>> = {
  0: "routing",
  1: "research",
  2: "profiles",
  3: "synthesis",
  4: "support",
};

/**
 * A single resolved agent-type → tier mapping. Returned by
 * `resolveAgentTiers()`; the rendered table in the orchestrator rule
 * content and the lookup table embedded in `check-blocked.sh` are
 * derived from this list.
 */
export interface ResolvedAgentTier {
  /**
   * GitHub `type:*` label value (e.g. `"feat"`, `"research"`,
   * `"company-profile"`). The leading `type:` prefix is **not** part
   * of the stored value — it is added when the resolver stringifies
   * the lookup table.
   */
  readonly type: string;
  /** Funnel tier 0–4 (lower dispatches first on a priority tie). */
  readonly tier: AgentTier;
  /** Human-readable tier role (routing / research / profiles / synthesis / support). */
  readonly role: string;
}

/**
 * Canonical default mapping of GitHub `type:*` label values to funnel
 * tiers. Mirrors openhi's `DISPATCHER.md` Dispatch Table plus the
 * taxonomy documented in the Group D epic (#414, issue #473).
 *
 * - **Tier 0 — routing.** Unblocks other work. Picked first on a
 *   priority tie. Covers the issue-worker's own type labels
 *   (`feat`, `fix`, `chore`, `refactor`, `docs`, `release`,
 *   `hotfix`). Unknown labels also fall through here via
 *   `UNKNOWN_TYPE_FALLBACK_TIER` so an unclassified issue never
 *   blocks the queue.
 * - **Tier 1 — research.** Feeds downstream pipelines.
 * - **Tier 2 — profiles.** Consumes research.
 * - **Tier 3 — synthesis.** Produces deliverables.
 * - **Tier 4 — support.** Important but not pipeline-critical.
 *
 * Consumers extend or override this list via
 * `AgentConfigOptions.tiers` (see `resolveAgentTiers`). Every
 * registered agent type must resolve to exactly one tier; unmapped
 * types fail synth-time validation.
 */
export const DEFAULT_AGENT_TIERS: ReadonlyArray<ResolvedAgentTier> = [
  // Tier 0 — routing (default branch for issue-worker type labels too,
  // so issues without a pipeline type still sort sensibly).
  { type: "feat", tier: 0, role: AGENT_TIER_ROLES[0] },
  { type: "fix", tier: 0, role: AGENT_TIER_ROLES[0] },
  { type: "chore", tier: 0, role: AGENT_TIER_ROLES[0] },
  { type: "refactor", tier: 0, role: AGENT_TIER_ROLES[0] },
  { type: "docs", tier: 0, role: AGENT_TIER_ROLES[0] },
  { type: "release", tier: 0, role: AGENT_TIER_ROLES[0] },
  { type: "hotfix", tier: 0, role: AGENT_TIER_ROLES[0] },

  // Tier 1 — research.
  { type: "research", tier: 1, role: AGENT_TIER_ROLES[1] },
  { type: "industry-discovery", tier: 1, role: AGENT_TIER_ROLES[1] },
  { type: "standards-research", tier: 1, role: AGENT_TIER_ROLES[1] },
  { type: "regulatory-research", tier: 1, role: AGENT_TIER_ROLES[1] },

  // Tier 2 — profiles.
  { type: "company-profile", tier: 2, role: AGENT_TIER_ROLES[2] },
  { type: "people-profile", tier: 2, role: AGENT_TIER_ROLES[2] },
  { type: "software-profile", tier: 2, role: AGENT_TIER_ROLES[2] },
  { type: "customer-profile", tier: 2, role: AGENT_TIER_ROLES[2] },

  // Tier 3 — synthesis.
  { type: "business-model", tier: 3, role: AGENT_TIER_ROLES[3] },
  { type: "bcm-document", tier: 3, role: AGENT_TIER_ROLES[3] },
  { type: "requirement", tier: 3, role: AGENT_TIER_ROLES[3] },

  // Tier 4 — support.
  { type: "meeting-processing", tier: 4, role: AGENT_TIER_ROLES[4] },
  { type: "agenda", tier: 4, role: AGENT_TIER_ROLES[4] },
  { type: "maintenance", tier: 4, role: AGENT_TIER_ROLES[4] },
  { type: "pr-review", tier: 4, role: AGENT_TIER_ROLES[4] },
  { type: "docs-sync", tier: 4, role: AGENT_TIER_ROLES[4] },
];

/**
 * Fallback tier applied to issues whose `type:*` label does not match
 * any entry in the resolved tier table. Using tier 0 (routing) means
 * an unclassified or newly-introduced issue is dispatched **before**
 * lower-tier work on a priority tie, which matches the openhi
 * dispatcher's "routing unblocks other work" rule. The synth-time
 * validator still rejects **registered** custom types that omit a
 * tier assignment — the fallback exists only for unknown labels
 * encountered at runtime.
 */
export const UNKNOWN_TYPE_FALLBACK_TIER: AgentTier = 0;

/**
 * Resolve a (possibly absent) consumer-supplied `AgentTierConfig` into
 * a deduplicated list of `ResolvedAgentTier` entries.
 *
 * Precedence:
 *
 * 1. `config.tiers` fully **replaces** the default list when supplied.
 *    This is the escape hatch for repos that want a bespoke taxonomy.
 * 2. `config.customTypes` entries **extend** whatever list is in play
 *    (default or replacement). Later entries override earlier ones on
 *    `type` collision, so consumer overrides win over defaults.
 *
 * @throws `Error` when any entry references a tier outside 0–4, when a
 *         `type` is empty/whitespace, or when `config.tiers` is an
 *         empty array (use omission to mean "keep the defaults").
 */
export function resolveAgentTiers(
  config?: AgentTierConfig,
): ReadonlyArray<ResolvedAgentTier> {
  const base =
    config?.tiers !== undefined
      ? validateTierList(config.tiers, "tiers")
      : DEFAULT_AGENT_TIERS;

  if (config?.tiers !== undefined && config.tiers.length === 0) {
    throw new Error(
      "AgentTierConfig.tiers must be omitted (to keep defaults) or contain at least one entry; got an empty array",
    );
  }

  const merged = new Map<string, ResolvedAgentTier>();
  for (const entry of base) {
    merged.set(entry.type, entry);
  }

  if (config?.customTypes !== undefined) {
    for (const entry of validateTierList(config.customTypes, "customTypes")) {
      merged.set(entry.type, entry);
    }
  }

  return [...merged.values()];
}

/**
 * Synth-time validation hook. Throws a descriptive `Error` when the
 * resolved tier list is malformed. Called by `AgentConfig` before
 * rendering so a misconfigured `AgentTierConfig` fails the build
 * instead of silently shipping broken sort keys.
 *
 * Malformed cases rejected here:
 *
 * - Duplicate `type` values **within** the same supplied list (the
 *   resolver dedupes between the default list and `customTypes`, so
 *   those collisions are fine; duplicates inside one list are not).
 * - Tier values outside 0–4.
 * - Empty or whitespace-only `type` strings.
 *
 * Returns the validated list unchanged so callers can write
 * `const tiers = validateAgentTierConfig(config)` in one line.
 */
export function validateAgentTierConfig(
  config?: AgentTierConfig,
): ReadonlyArray<ResolvedAgentTier> {
  if (config?.tiers !== undefined) {
    assertNoDuplicates(config.tiers, "tiers");
  }
  if (config?.customTypes !== undefined) {
    assertNoDuplicates(config.customTypes, "customTypes");
  }
  return resolveAgentTiers(config);
}

/**
 * Render the funnel-tier subsection appended to the
 * `orchestrator-conventions` rule. Always returns a non-empty string
 * because the default tier list is non-empty — the orchestrator
 * always documents its sort order.
 *
 * When `excludeBundles` is non-empty, rows whose `type:*` label is
 * owned by an excluded bundle are dropped before rendering. Tiers
 * that end up with no surviving rows render an empty
 * `_(none registered)_` cell for consistency with the existing
 * render — the tier itself stays in the table so the dispatch
 * ordering is still documented end-to-end.
 */
export function renderAgentTierSection(
  tiers: ReadonlyArray<ResolvedAgentTier>,
  excludeBundles: ReadonlyArray<string> = [],
): string {
  const filtered = tiers.filter(
    (entry) => !isTypeLabelOwnedByExcluded(entry.type, excludeBundles),
  );

  const grouped = new Map<AgentTier, Array<ResolvedAgentTier>>();
  for (const entry of filtered) {
    const bucket = grouped.get(entry.tier) ?? [];
    bucket.push(entry);
    grouped.set(entry.tier, bucket);
  }

  const lines: Array<string> = [
    "## Funnel-tier dispatch ordering",
    "",
    "The orchestrator sorts eligible issues by **priority desc → tier asc → issue",
    "number asc**. Lower tier numbers win ties on priority so research feeders",
    "never starve while downstream synthesis consumes attention.",
    "",
    "| Tier | Role | Registered agent types |",
    "|------|------|------------------------|",
  ];

  for (const tier of AGENT_TIER_VALUES) {
    const bucket = grouped.get(tier) ?? [];
    const typeList =
      bucket.length === 0
        ? "_(none registered)_"
        : bucket.map((e) => `\`type:${e.type}\``).join(", ");
    lines.push(`| ${tier} | ${AGENT_TIER_ROLES[tier]} | ${typeList} |`);
  }

  lines.push(
    "",
    `Unknown \`type:*\` labels encountered at runtime fall through to tier ${UNKNOWN_TYPE_FALLBACK_TIER} (${AGENT_TIER_ROLES[UNKNOWN_TYPE_FALLBACK_TIER]}) — the routing bucket — so an unclassified issue never blocks the queue.`,
  );

  return lines.join("\n");
}

/**
 * Render a shell-script snippet that the `check-blocked.sh` procedure
 * uses to look up a tier for a given `type:*` label. Emitted as a
 * `case` statement so the generated script stays POSIX-shell
 * compatible (no associative arrays).
 *
 * The function returns the body of a shell function, not the
 * surrounding function wrapper, so bundle code can splice it inline
 * at the exact indent level it wants.
 */
export function renderAgentTierCaseStatement(
  tiers: ReadonlyArray<ResolvedAgentTier>,
): string {
  const lines: Array<string> = [];
  lines.push('  case "$1" in');
  for (const entry of tiers) {
    lines.push(`    ${entry.type}) echo ${entry.tier} ;;`);
  }
  lines.push(`    *) echo ${UNKNOWN_TYPE_FALLBACK_TIER} ;;`);
  lines.push("  esac");
  return lines.join("\n");
}

/*******************************************************************************
 *
 * Internal helpers
 *
 ******************************************************************************/

function validateTierList(
  entries: ReadonlyArray<{ readonly type: string; readonly tier: number }>,
  field: "tiers" | "customTypes",
): ReadonlyArray<ResolvedAgentTier> {
  return entries.map((entry) => {
    const trimmed = entry.type?.trim();
    if (!trimmed) {
      throw new Error(
        `AgentTierConfig.${field} entry has an empty or whitespace-only \`type\`; every entry must declare a non-empty type label`,
      );
    }
    if (!isAgentTier(entry.tier)) {
      throw new Error(
        `AgentTierConfig.${field} entry for \`type:${trimmed}\` has tier ${entry.tier}; tiers must be one of ${AGENT_TIER_VALUES.join(", ")}`,
      );
    }
    return {
      type: trimmed,
      tier: entry.tier,
      role: AGENT_TIER_ROLES[entry.tier],
    };
  });
}

function assertNoDuplicates(
  entries: ReadonlyArray<{ readonly type: string }>,
  field: "tiers" | "customTypes",
): void {
  const seen = new Set<string>();
  for (const entry of entries) {
    const trimmed = entry.type?.trim() ?? "";
    if (!trimmed) {
      // Emptiness is validated in validateTierList; skip here so the
      // duplicate check never matches two empty strings.
      continue;
    }
    if (seen.has(trimmed)) {
      throw new Error(
        `AgentTierConfig.${field} declares \`type:${trimmed}\` more than once; every type must appear at most once within a single list`,
      );
    }
    seen.add(trimmed);
  }
}

function isAgentTier(value: number): value is AgentTier {
  return (AGENT_TIER_VALUES as ReadonlyArray<number>).includes(value);
}
